import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import { IDocumentInfo, ILocale } from 'interfaces';
import blobErrorToObject from 'helpers/blobErrorToObject';
import { SigningState } from './signing';
import users from './users';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const converterTemplate = axios.create({
  baseURL: window.REACT_APP_CONVERTER_API !== 'REPLACE_REACT_APP_CONVERTER_API' ? window.REACT_APP_CONVERTER_API : process.env.REACT_APP_CONVERTER_API || 'http://localhost:8010/api',
  // withCredentials: true,
  headers: correctHeaders,
});

converterTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();

    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');      
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };

    config.headers!.Authorization = `Bearer ${accessToken}`
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

converterTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 1) {
    cntTryingUpdateFingerprint++;

    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');

      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return converterTemplate.request(error.request.responseURL);
    } catch {
      resetTokensAndSendToLogin();
    }
  } else if (cntTryingUpdateFingerprint >= 1) {
    resetTokensAndSendToLogin();
  };

  if (error?.response?.data.constructor.name === 'Blob'){
    error.response.data = await blobErrorToObject(error?.response?.data)
  };
  return Promise.reject(error)
});

const convertDocument = (payload: IDocumentInfo & ILocale) => converterTemplate.post<Blob>('/converter/pdf/', payload, { responseType: 'blob' });

const getConfidentReportPDF = (room_id: string, locale: string, type?: string) => converterTemplate.post<Blob>(`/converter/confident/report/pdf/?locale=${locale}`,
  { room_id, type }, { responseType: 'blob' }
);

// SIGNING
const addSigning = (
  payload: SigningState
) => converterTemplate.post<string>(`/signing/`, payload);

const changeSigntStatus = <T = any> (
  payload: { document_id: string, sign_password: string },
) => converterTemplate.post<T>(`/signing/sign/`, payload);

const requestSigning = (
  payload: {document_id: string}
) => converterTemplate.post<string>(`/signing/request_signing/`, payload);

const convert = {
  convertDocument,
  getConfidentReportPDF,
  addSigning,
  changeSigntStatus,
  requestSigning,
};

export default convert;
